import { useTranslation } from 'react-i18next';
import {
    Box,
    VStack,
    HStack,
    Text,
    Input,
    Button,
    Badge,
    Avatar,
    Divider,
    IconButton,
    Tooltip,

} from '@chakra-ui/react';
import { useState, useRef, useEffect } from 'react';
import { AppDialog } from '../common/AppDialog';
import { FiUsers, FiSend, FiLogIn, FiLogOut } from 'react-icons/fi';
import { useCollabStore, useCollaboration, CollaboratorInfo } from '../../collaboration';
import { useAppStore } from '../../state';

/**
 * Collaboration Panel Trigger Component
 *
 * Standalone trigger of the collaboration room (currently unmounted — the
 * suite topbar's Collaborate action opens the room in every workbench). The
 * dialog itself lives in CollabRoomDialog (controlled by useAppStore.
 * collabRoomOpen) so the room can be opened from any surface.
 */
export function CollabPanel() {
    const { t } = useTranslation();
    const { isConnected, roomId, collaboratorCount } = useCollaboration();
    const setCollabRoomOpen = useAppStore((s) => s.setCollabRoomOpen);

    return (
        <>
            {/* Toolbar Button */}
            <Tooltip label={isConnected ? `Connected: ${roomId}` : 'Collaborate'}>
                <IconButton
                    icon={<FiUsers />}
                    variant="ghost"
                    size="sm"
                    aria-label={t('collab.collaboration')}
                    onClick={() => setCollabRoomOpen(true)}
                    color={isConnected ? 'green.400' : undefined}
                />
            </Tooltip>

            {isConnected && collaboratorCount > 0 && (
                <Badge colorScheme="green" ml={1}>{collaboratorCount + 1}</Badge>
            )}
        </>
    );
}

/**
 * Collaboration Room Dialog Component
 *
 * Provides:
 * - Room connection/disconnection
 * - Collaborator list with avatars
 * - Real-time chat
 */
export function CollabRoomDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((s) => s.collabRoomOpen);
    const setCollabRoomOpen = useAppStore((s) => s.setCollabRoomOpen);
    const onClose = () => setCollabRoomOpen(false);
    const { isConnected, roomId, collaboratorCount, collaborators, connect, disconnect } = useCollaboration();
    const [roomInput, setRoomInput] = useState('');
    const [chatInput, setChatInput] = useState('');
    const chatMessages = useCollabStore((s) => s.chatMessages);
    const sendChatMessage = useCollabStore((s) => s.sendChatMessage);
    const userName = useCollabStore((s) => s.userName);
    const setUserName = useCollabStore((s) =>s.setUserName); const chatContainerRef = useRef<HTMLDivElement>(null);

    // Auto-scroll chat
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [chatMessages]);

    const handleConnect = () => {
        if (roomInput.trim()) {
            connect(roomInput.trim());
        }
    };

    const handleDisconnect = () => {
        disconnect();
    };

    const handleSendChat = () => {
        if (chatInput.trim()) {
            sendChatMessage(chatInput.trim());
            setChatInput('');
        }
    };

    return (
        <AppDialog isOpen={isOpen} onClose={onClose} size="md" icon={<FiUsers />} title={t('collab.collaboration')}>
                        <VStack spacing={4} align="stretch">
                            {isConnected && (
                                <Badge alignSelf="flex-start" colorScheme="green">{t('collab.connected')}</Badge>
                            )}
                            {/* Connection Section */}
                            {!isConnected ? (
                                <VStack spacing={3} align="stretch">
                                    <Text fontSize="sm" color="gray.400">{t('collab.enterARoomNameToStartCollaborating')}</Text>

                                    <Input
                                        placeholder={t('collab.yourName')}
                                        value={userName}
                                        onChange={(e) => setUserName(e.target.value)}
                                        size="sm"
                                        variant="filled"
                                        bg="surface.300"
                                    />

                                    <HStack>
                                        <Input
                                            placeholder={t('collab.roomName')}
                                            value={roomInput}
                                            onChange={(e) => setRoomInput(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
                                            size="sm"
                                            variant="filled"
                                            bg="surface.300"
                                        />
                                        <Button
                                            leftIcon={<FiLogIn />}
                                            colorScheme="blue"
                                            size="sm"
                                            onClick={handleConnect}
                                            isDisabled={!roomInput.trim()}
                                        >{t('collab.join')}</Button>
                                    </HStack>
                                </VStack>
                            ) : (
                                <>
                                    {/* Connected Info */}
                                    <Box bg="surface.300" p={3} borderRadius="md">
                                        <HStack justify="space-between">
                                            <VStack align="start" spacing={0}>
                                                <Text fontSize="xs" color="gray.500">{t('collab.room')}</Text>
                                                <Text fontWeight="medium">{roomId}</Text>
                                            </VStack>
                                            <Button
                                                leftIcon={<FiLogOut />}
                                                colorScheme="red"
                                                variant="outline"
                                                size="sm"
                                                onClick={handleDisconnect}
                                            >{t('collab.leave')}</Button>
                                        </HStack>
                                    </Box>

                                    <Divider />

                                    {/* Collaborators List */}
                                    <Box>
                                        <Text fontSize="xs" fontWeight="semibold" color="gray.400" mb={2}>
                                            COLLABORATORS ({collaboratorCount + 1})
                                        </Text>
                                        <VStack spacing={2} align="stretch">
                                            {/* Self */}
                                            <CollaboratorRow
                                                info={{
                                                    id: 'self',
                                                    name: `${userName} (You)`,
                                                    color: useCollabStore.getState().userColor,
                                                    lastActive: Date.now(),
                                                }}
                                                isSelf
                                            />

                                            {/* Others */}
                                            {collaborators.map((collab) => (
                                                <CollaboratorRow key={collab.id} info={collab} />
                                            ))}
                                        </VStack>
                                    </Box>

                                    <Divider />

                                    {/* Chat */}
                                    <Box>
                                        <Text fontSize="xs" fontWeight="semibold" color="gray.400" mb={2}>{t('collab.cHAT')}</Text>

                                        {/* Messages */}
                                        <Box
                                            ref={chatContainerRef}
                                            h="150px"
                                            overflowY="auto"
                                            bg="surface.300"
                                            borderRadius="md"
                                            p={2}
                                            mb={2}
                                        >
                                            {chatMessages.length === 0 ? (
                                                <Text fontSize="xs" color="gray.500" textAlign="center">{t('collab.noMessagesYet')}</Text>
                                            ) : (
                                                chatMessages.map((msg, i) => (
                                                    <Box key={i} mb={1}>
                                                        <Text fontSize="xs">
                                                            <Text as="span" fontWeight="bold" color="blue.300">
                                                                {msg.sender}:
                                                            </Text>{' '}
                                                            {msg.message}
                                                        </Text>
                                                    </Box>
                                                ))
                                            )}
                                        </Box>

                                        {/* Input */}
                                        <HStack>
                                            <Input
                                                placeholder={t('collab.typeAMessage')}
                                                value={chatInput}
                                                onChange={(e) => setChatInput(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
                                                size="sm"
                                                variant="filled"
                                                bg="surface.300"
                                            />
                                            <IconButton
                                                icon={<FiSend />}
                                                aria-label={t('collab.send')}
                                                size="sm"
                                                colorScheme="blue"
                                                onClick={handleSendChat}
                                                isDisabled={!chatInput.trim()}
                                            />
                                        </HStack>
                                    </Box>
                                </>
                            )}
                        </VStack>
        </AppDialog>
    );
}

/**
 * Collaborator Row Component
 */
function CollaboratorRow({ info, isSelf }: { info: CollaboratorInfo; isSelf?: boolean }) {
    const isActive = Date.now() - info.lastActive < 5000;

    return (
        <HStack
            p={2}
            bg={isSelf ? 'whiteAlpha.100' : 'transparent'}
            borderRadius="md"
            spacing={3}
        >
            <Avatar
                size="xs"
                name={info.name}
                bg={info.color}
            />
            <Text flex="1" fontSize="sm" noOfLines={1}>
                {info.name}
            </Text>
            {!isSelf && (
                <Box
                    w={2}
                    h={2}
                    borderRadius="full"
                    bg={isActive ? 'green.400' : 'gray.500'}
                />
            )}
        </HStack>
    );
}
